import axios from 'axios'
import { get } from 'lodash'
import { HYDRA_MEMBER } from '../configuration/constants'
import {
  getHydraMemberDataType,
  recursiveFixApiPayload
} from '../configuration/utilities'
import { defaultHeaders, requiredEmptyUserStrings } from './constants'
import { FORCE_LOWERCASE_EMAIL } from '../configuration/constants'

// TOP LEVEL ENDPOINT TYPES
const CONFIG = '/api/config'
const CONTENT = '/api/content'
const FERRY = '/api/ferry'

const newEndpoint = string => `${process.env.REACT_APP_ENDPOINT_DOLLI}${string}`

const endpoints = {
  activeSession: newEndpoint(`${FERRY}/active-new-session`),
  agentUser: newEndpoint(`${FERRY}/agent-user`),
  booking: newEndpoint(`${FERRY}/booking`),
  configuration: newEndpoint(`${CONFIG}?`),
  content: newEndpoint(`${CONTENT}/ferry-sailing?`),
  crossing: newEndpoint(`${FERRY}/crossing`),
  coupon: newEndpoint(`${FERRY}/coupon`),
  coPassengers: newEndpoint(`${FERRY}/copassengers/update`),
  customer: newEndpoint(`${FERRY}/customer`),
  customers: newEndpoint(`${FERRY}/customers`),
  passwordReset: newEndpoint(`${FERRY}/customer/password-reset`),
  payment: newEndpoint(`${FERRY}/booking/payment`),
  preloadTransaction: newEndpoint(`${FERRY}/booking/checkout/preload`),
  transactionReceipt: newEndpoint(`${FERRY}/booking/checkout/receipt`),
  telephoneBooking: newEndpoint(`${FERRY}/phone-booking`),
  findReservation: newEndpoint(`${FERRY}/bookings/reservation`)
}

export const getConfiguration = params =>
  axios.all([
    axios.get(endpoints.configuration + params.toString()),
    axios.get(endpoints.content + params.toString())
  ])

export const crossing = params => {
  return axios.get(endpoints.crossing, { params })
}

export const getCustomerAccount = ({ emailAddress, clientId, sessionId }) => {
  return axios.post(
    `${endpoints.customers}/search?clientId=${clientId}&sessionId=${sessionId}`,
    JSON.stringify({ identifier: emailAddress }),
    { headers: { ...defaultHeaders } }
  )
}

export const sendEmailConfirmation = ({
  bookingNumber,
  clientId,
  sessionId,
  emailAddress
}) =>
  axios.put(
    `${endpoints.booking}/${bookingNumber}/send-email-confirmation?clientId=${clientId}&sessionId=${sessionId}`,
    JSON.stringify({
      sendConfirmationEmailParameters: {
        emailAddress: formatEmailParam(emailAddress)
      }
    }),
    { headers: { ...defaultHeaders } }
  )

export const getWebsiteStatus = ({ availabilityLink }) => {
  return axios.get(availabilityLink, { headers: { ...defaultHeaders } })
}

export const getActiveSessions = ({ sessionId }) => {
  return axios.get(
    `${endpoints.activeSession}?sessionId=${sessionId}&clientId=${process.env.REACT_APP_FLOW_CLIENT_ID}`,
    {
      headers: { ...defaultHeaders }
    }
  )
}

export const fetchCustomerBookings = ({ id, sessionId, start, end }) => {
  const params = new URLSearchParams()

  params.append('clientId', process.env.REACT_APP_FLOW_CLIENT_ID)
  params.append('sessionId', sessionId)

  if (start) {
    params.append('fromDate', start)
  }
  if (end) {
    params.append('toDate', end)
  }

  return axios.get(
    `${endpoints.customer}/${encodeURIComponent(
      id
    )}/bookings?${params.toString()}`,
    { headers: { ...defaultHeaders } }
  )
}

export const getCouponInformation = ({
  code,
  sessionId,
  departureDate,
  returnDate
}) => {
  const params = new URLSearchParams()

  params.append('clientId', process.env.REACT_APP_FLOW_CLIENT_ID)
  params.append('sessionId', sessionId)
  params.append('departureDate', departureDate)

  if (returnDate) {
    params.append('returnDate', returnDate)
  }

  return axios.get(
    `${endpoints.coupon}/${encodeURIComponent(code)}?${params.toString()}`,
    {
      headers: { ...defaultHeaders }
    }
  )
}

export const travelAgentGetAccount = ({ userId, clientId, sessionId }) =>
  axios.get(
    `${endpoints.agentUser}/${userId}?clientId=${clientId}&sessionId=${sessionId}`,
    { headers: { ...defaultHeaders } }
  )

export const travelAgentModifyAccount = ({
  userName,
  sessionId,
  ...restValues
}) =>
  axios.put(
    `${endpoints.agentUser}/${userName}/modify?clientId=${process.env.REACT_APP_FLOW_CLIENT_ID}&sessionId=${sessionId}`,
    JSON.stringify({
      modifyParameters: [{ ...restValues }]
    }),
    { headers: { ...defaultHeaders } }
  )

export const travelAgentLogin = ({ userId, clientId, sessionId, password }) => {
  const postBody = JSON.stringify({
    loginParameters: {
      id: userId,
      password: password
    }
  })

  return axios.post(
    `${endpoints.agentUser}/login?clientId=${clientId}&sessionId=${sessionId}`,
    postBody,
    { headers: { ...defaultHeaders } }
  )
}

export const travelAgentGetBookings = ({
  agentNumber,
  clientId,
  sessionId,
  start,
  end
}) => {
  const params = new URLSearchParams()

  params.append('clientId', process.env.REACT_APP_FLOW_CLIENT_ID)
  params.append('sessionId', sessionId)

  if (start) {
    params.append('fromDate', start)
  }
  if (end) {
    params.append('toDate', end)
  }

  return axios.get(
    `${endpoints.agentUser}/${agentNumber}/bookings?${params.toString()}`,
    { headers: { ...defaultHeaders } }
  )
}

/**
 * login with POST method
 * for https://jira.verbinteractive.com/browse/CTM200-559
 *
 * @param {object}  object { emailAddress, password, sessionId }
 * @returns Promise
 *
 * sample:
 * curl -X POST "https://http://dev-ferries.dolli.cloud/api/ferry/customer/login?sessionId=XXXXXXXXXX&clientId=ctma" \
 *  -H  "accept: application/ld+json" -H  "Content-Type: application/ld+json" \
 *  -d "{  \"loginParameters\": {    \"id\": \"username@domain.com\",    \"password\": \"%123456Test\"  }}"
 */
export const login = ({ emailAddress, clientId, sessionId, password }) => {
  const postBody = JSON.stringify({
    loginParameters: {
      id: formatEmailParam(emailAddress),
      password: password
    }
  })
  return axios.post(
    `${endpoints.customer}/login?clientId=${clientId}&sessionId=${sessionId}`,
    postBody,
    { headers: { ...defaultHeaders } }
  )
}

export const bookingTransactionReceipt = (params = {}, clientId, sessionId) =>
  axios.post(
    `${endpoints.transactionReceipt}?clientId=${clientId}&sessionId=${sessionId}`,
    JSON.stringify({ createParameters: { ...params } }),
    { headers: { ...defaultHeaders } }
  )

export const apiBookingTransactionReceipt = ({
  sessionId,
  clientId,
  ...params
}) =>
  bookingTransactionReceipt({ ...params }, clientId, sessionId)
    .then(response => {
      const data = response?.data?.['hydra:member']?.[0]
      let result = { error: 'true', success: {} }

      if (data?.success === 'true' && data?.receipt && data?.id) {
        result = { error: '', success: { receipt: data.receipt, id: data.id } }
      }

      return result
    })
    .catch(() => ({ error: 'true', success: {} }))

export const preloadBookingTransaction = (params = {}, clientId, sessionId) =>
  axios.post(
    `${endpoints.preloadTransaction}?clientId=${clientId}&sessionId=${sessionId}`,
    JSON.stringify({ createParameters: { ...params } }),
    { headers: { ...defaultHeaders } }
  )

export const apiPreloadBookingTransaction = ({
  sessionId,
  clientId,
  ...params
}) =>
  preloadBookingTransaction({ ...params }, clientId, sessionId)
    .then(response => {
      const data = response?.data?.['hydra:member']?.[0]
      let result = { error: 'true', success: {} }

      if (data?.success === 'true' && data?.ticket && data?.id) {
        result = { error: '', success: { ticket: data.ticket, id: data.id } }
      } else {
        if (
          typeof data?.errors === 'object' &&
          Object.keys(data.errors).length > 0
        ) {
          if (data.errors?.order_no) {
            result.error = 'Duplicate Order'
          }

          if (data.errors?.txn_total) {
            result.error = 'Invalid params'
          }
        }
      }

      return result
    })
    .catch(() => ({ error: 'true', success: {} }))

export const book = ({ params = {}, clientId, sessionId }) =>
  axios.post(
    `${endpoints.booking}?clientId=${clientId}&sessionId=${sessionId}`,
    JSON.stringify({ createParameters: [{ ...params }] }),
    { headers: { ...defaultHeaders } }
  )

export const modifyBooking = ({
  params = {},
  clientId,
  sessionId,
  modificationId
}) =>
  axios.put(
    `${endpoints.booking}/${modificationId}/modify?clientId=${clientId}&sessionId=${sessionId}`,
    JSON.stringify({ modifyParameters: [{ ...params }] }),
    { headers: { ...defaultHeaders } }
  )

export const asyncModifyBooking = async (modifyDetails = {}) =>
  new Promise(resolve => {
    modifyBooking({ ...modifyDetails })
      .then(res => {
        const data = get(res, ['data', HYDRA_MEMBER, '0'])
        resolve({ ...data, success: true } || { success: false, data })
      })
      .catch(error => {
        resolve({ success: false, data: error.response })
      })
  })

export const cancelBooking = ({ id, sessionId, params }) =>
  axios.put(
    `${endpoints.booking}/${id}/cancel?clientId=${process.env.REACT_APP_FLOW_CLIENT_ID}&sessionId=${sessionId}`,
    JSON.stringify({ modifyParameters: [{ ...params }] }),
    { headers: { ...defaultHeaders } }
  )

export const register = (formValues = {}, sessionId) => {
  return axios.post(
    `${endpoints.customer}?clientId=${process.env.REACT_APP_FLOW_CLIENT_ID}&sessionId=${sessionId}&lang=en`,
    JSON.stringify({ createParameters: [{ ...formValues }] }),
    { headers: { ...defaultHeaders } }
  )
}

export const apiRegister = ({ sessionId, ...restValues }) => {
  const formattedEmailAddress = formatEmailParam(restValues.emailAddress)
  // check city and address
  recursiveFixApiPayload(restValues)

  return register(
    {
      ...requiredEmptyUserStrings,
      ...restValues,
      emailAddress: formattedEmailAddress
    },
    sessionId
  )
    .then(success => {
      if (restValues.emailAddress) {
        const emailAddressExpected =
          process.env.REACT_APP_FLOW_CLIENT_ID === 'ctma'
            ? restValues.emailAddress.toLowerCase()
            : restValues.emailAddress
        const successData = getHydraMemberDataType(
          success,
          emailAddressExpected
        )

        if (successData && successData.customerAccount) {
          return { success: successData.customerAccount, error: false }
        }
      }

      return { error: true, success: false }
    })
    .catch(async errorResponse => {
      const customerExists = await checkCustomerAccountExists(
        formattedEmailAddress,
        sessionId
      )

      if (customerExists) {
        return { errorKey: 'emailAddressAlreadyExists', success: false }
      }

      let errorKey = 'defaultRegisterUserError'
      if (
        errorResponse?.response?.status &&
        errorResponse.response.status.toString().startsWith('4')
      ) {
        const errorMessage =
          errorResponse?.response?.data?.['hydra:description']

        if (errorMessage.includes('E-mails already exists in Bookit!')) {
          errorKey = 'emailAddressAlreadyExists'
        }

        if (errorMessage.includes('The password is too short')) {
          errorKey = 'passwordTooShort'
        }
        if (errorMessage.includes('The password is too long')) {
          errorKey = 'passwordTooLong'
        }
        if (
          errorMessage.includes('The password does not match criteria') ||
          errorMessage.includes('Password is invalid!')
        ) {
          errorKey = 'passwordFailedCriteria'
        }
      }

      return { errorKey, success: false }
    })
}

export const modifyCustomer = ({
  customerNumber,
  sessionId,
  ...restValues
}) => {
  return axios.put(
    `${endpoints.customer}/${customerNumber}/modify?clientId=${process.env.REACT_APP_FLOW_CLIENT_ID}&sessionId=${sessionId}`,
    JSON.stringify({
      modifyParameters: [
        {
          ...requiredEmptyUserStrings,
          ...restValues,
          emailAddress: formatEmailParam(restValues.emailAddress)
        }
      ]
    }),
    { headers: { ...defaultHeaders } }
  )
}

export const sendResetPasswordEmail = ({
  emailAddress,
  clientId,
  sessionId,
  language,
  url
}) =>
  axios.post(
    `${endpoints.passwordReset}?clientId=${clientId}&sessionId=${sessionId}`,
    JSON.stringify({
      createPasswordResetParameters: {
        email: formatEmailParam(emailAddress),
        url,
        locale: language
      }
    }),
    { headers: { ...defaultHeaders } }
  )

export const resetPassword = ({ password, clientId, sessionId, emailToken }) =>
  axios.put(
    `${endpoints.passwordReset}/token/${emailToken}?clientId=${clientId}&sessionId=${sessionId}`,
    JSON.stringify({
      modifyPasswordResetParameters: {
        password
      }
    }),
    { headers: { ...defaultHeaders } }
  )

export const postPayment = (params, sessionId) =>
  axios.post(
    `${endpoints.payment}?clientId=${process.env.REACT_APP_FLOW_CLIENT_ID}&sessionId=${sessionId}`,
    JSON.stringify({ createParameters: [params] }),
    {
      headers: { ...defaultHeaders }
    }
  )

export const fetchPhoneBooking = (id, ferryId, sessionId) => {
  const params = new URLSearchParams()
  params.append('sessionId', sessionId)
  params.append('clientId', process.env.REACT_APP_FLOW_CLIENT_ID)

  if (ferryId) {
    params.append('ferryId', ferryId)
  }

  return axios.get(`${endpoints.telephoneBooking}/${id}?${params.toString()}`)
}

export const postCoPassengers = async (
  coPassengers,
  customerNumber,
  sessionId
) => {
  const updateParameters = [
    {
      customerNumber,
      coPassengers
    }
  ]
  const params = new URLSearchParams()
  params.append('sessionId', sessionId)
  params.append('clientId', process.env.REACT_APP_FLOW_CLIENT_ID)

  const response = await axios.post(
    `${endpoints.coPassengers}?${params.toString()}`,
    JSON.stringify({ updateParameters }),
    {
      headers: { ...defaultHeaders }
    }
  )

  const data = response?.data?.['hydra:member']?.[0]
  return data.coPassengers
}

// bookingNumbers is "Comma delimited list of booking numbers to retrieve"
export const fetchBookingsById = ({ ids, sessionId }) => {
  return axios.get(
    `${endpoints.booking}?bookingNumbers=${ids}&clientId=${process.env.REACT_APP_FLOW_CLIENT_ID}&sessionId=${sessionId}`
  )
}

const formatServerError = (caughtError, defaultError = {}) => {
  const result = { error: defaultError }

  if (caughtError?.response) {
    const { data, status } = caughtError.response

    if (data && status) {
      result.error.code = status

      if (data['hydra:title']) result.error.title = data['hydra:title']
      if (data['hydra:description']) {
        result.error.description = data['hydra:description']
      }
    }
  }

  return result
}

export const asyncPostBooking = ({ params, clientId, sessionId }) => {
  params.billingInfo = {
    ...params.billingInfo,
    email: formatEmailParam(params.billingInfo.email)
  }
  // check city and address
  recursiveFixApiPayload(params)

  return new Promise(resolve => {
    let result = { success: false, successData: {}, error: {} }

    book({ params, clientId, sessionId })
      .then(res => {
        const data = get(res, ['data', HYDRA_MEMBER, '0'])

        if (data?.bookingNumber) {
          const { bookingNumber, address, ...restSuccessData } = data
          result.success = true
          // dont store phony data or bookingNumber
          result.successData =
            bookingNumber === '-1' ? { ...restSuccessData } : { ...data }
        }
      })
      .catch(caughtError => {
        result = { ...result, ...formatServerError(caughtError) }
      })
      .then(() => {
        // Note: always executed
        resolve(result)
      })
  })
}

export const asyncPostModifyBooking = ({
  params,
  clientId,
  sessionId,
  modificationId,
  commitChanges
}) => {
  params.billingInfo = {
    ...params.billingInfo,
    email: formatEmailParam(params.billingInfo.email)
  }

  // check city and address
  recursiveFixApiPayload(params)

  return new Promise(resolve => {
    let result = { success: false, successData: {}, error: {} }

    modifyBooking({
      params,
      clientId,
      sessionId,
      modificationId
    })
      .then(res => {
        const data = get(res, ['data', HYDRA_MEMBER, '0'])
        if (data) {
          result.success = true
          result.successData = {
            ...data,
            isModification: true,
            modificationId,
            commitChanges
          }
        }
      })
      .catch(caughtError => {
        result = { ...result, ...formatServerError(caughtError) }
      })
      .then(() => {
        // Note: always executed
        resolve(result)
      })
  })
}

export const findReservation = (email, reservationId, sessionId) =>
  axios.post(
    `${endpoints.findReservation}?clientId=${process.env.REACT_APP_FLOW_CLIENT_ID}&sessionId=${sessionId}`,
    JSON.stringify({ email, reservationId }),
    {
      headers: { ...defaultHeaders }
    }
  )

// Local functions
async function checkCustomerAccountExists(emailAddress, sessionId) {
  const formattedEmail = formatEmailParam(emailAddress)
  const searchResponse = await getCustomerAccount({
    emailAddress: formattedEmail,
    clientId: process.env.REACT_APP_FLOW_CLIENT_ID,
    sessionId
  })
  return searchResponse.data['hydra:member'][0].id === 200
}

function formatEmailParam(email = '') {
  return FORCE_LOWERCASE_EMAIL ? email.toLowerCase() : email
}

export default endpoints
